import { useEffect, useState } from 'react';

import { trpc, TrpcOutputs } from '../../config/trpc';

type DocumentRow = TrpcOutputs['getDocumentsByRequestId'][0];

/**
 * `getDocumentsByRequestId` includes the `account` relation, but the `onDocument*` subscriptions emit a
 * bare `Document`, so rows appended after the initial fetch have no `account`.
 */
type DocumentOutput = Omit<DocumentRow, 'account'> & { account?: DocumentRow['account'] };
type UseDocuments = [DocumentOutput[], boolean, string?];

export const useDocuments = (requestId?: number, accountId?: number): UseDocuments => {
  const [documents, setDocuments] = useState<DocumentOutput[]>([]);
  const { data, isLoading, error } = trpc.getDocumentsByRequestId.useQuery(
    { requestId, accountId },
    { refetchOnWindowFocus: false },
  );

  useEffect(() => {
    if (data) {
      setDocuments(data);
    }
  }, [data]);

  trpc.onDocumentCreated.useSubscription(
    { requestId },
    {
      onData: (data) => {
        setDocuments((prev) => [...prev, data]);
      },
      onError: () => {},
    },
  );

  trpc.onDocumentUpdated.useSubscription(
    { requestId },
    {
      onData: (data) => {
        setDocuments((prev) =>
          prev.map((document) => {
            if (document.id === data.id) return data;
            return document;
          }),
        );
      },
      onError: (error) => {
        console.log('onDocumentUpdated.error: ', error);
      },
    },
  );

  trpc.onDocumentDeleted.useSubscription(
    { requestId },
    {
      onData: (deletedId) => {
        setDocuments((prev) => prev.filter((document) => document.id !== deletedId));
      },
      onError: (error) => {
        console.log('onDocumentDeleted.error: ', error);
      },
    },
  );

  return [documents, isLoading, error?.message];
};
